import { Link } from 'react-router-dom';
import { IoChevronForward, IoLocationOutline } from 'react-icons/io5';
import type { Contact } from '@pelipost/consumer-api-client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/ui/utils';

/**
 * Single contact row used on /recipients and the TopBar selector overlay.
 *
 * Variants:
 *   - 'row'      — full-width row with chevron → links to /recipients/:id
 *   - 'compact'  — inline, no chevron; used inside a Select dropdown item
 *
 * A status badge appears for non-active recipients (released/transferred/etc.)
 * so users aren't surprised when they try to send mail and it bounces.
 */

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  released: 'Released',
  transferred: 'Transferred',
  deceased: 'Deceased',
  not_found: 'Not found',
};

export interface ContactCardProps {
  contact: Contact;
  variant?: 'row' | 'compact';
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

export function ContactCard({
  contact,
  variant = 'row',
  selected = false,
  onClick,
  className,
}: ContactCardProps) {
  const fullName = `${contact.firstName} ${contact.lastName}`.trim();
  const statusLabel = STATUS_LABEL[contact.status];
  const isActive = contact.status === 'active';

  const inner = (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-[14px] text-[var(--text)] truncate">
            {fullName || 'Unnamed recipient'}
          </p>
          {!isActive && statusLabel ? (
            <Badge variant="outline" className="shrink-0">
              {statusLabel}
            </Badge>
          ) : null}
        </div>
        <p className="text-[12px] text-[var(--text-light)] truncate">
          #{contact.inmateNumber || '—'} · {contact.facilityName || 'Facility'}
        </p>
        {variant === 'row' && contact.facilityCity ? (
          <p className="mt-1 inline-flex items-center gap-1 text-[12px] text-[var(--text-muted)]">
            <IoLocationOutline size={12} aria-hidden />
            {contact.facilityCity}
            {contact.facilityState ? `, ${contact.facilityState}` : ''}
          </p>
        ) : null}
      </div>
      {variant === 'row' ? (
        <IoChevronForward
          size={18}
          className="text-[var(--text-muted)] shrink-0"
          aria-hidden
        />
      ) : null}
    </>
  );

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left',
          'hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
          selected && 'bg-[var(--brand-light)]',
          className,
        )}
        aria-pressed={selected}
      >
        {inner}
      </button>
    );
  }

  return (
    <Card
      className={cn(
        'p-0 overflow-hidden transition-colors',
        selected && 'ring-2 ring-[var(--brand)]',
        className,
      )}
    >
      <Link
        to={`/app/recipients/${contact.id}`}
        className={cn(
          'flex items-center gap-3 px-4 py-3 min-h-[64px]',
          'hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        )}
      >
        {inner}
      </Link>
    </Card>
  );
}
